import * as React from 'react';
import * as c from 'models/constants';
import { translate } from 'react-i18next';
import { reduxForm } from 'redux-form';
import { browserHistory, RouteComponentProps } from 'react-router';
import { Dispatch } from 'redux';
const { connect } = require('react-redux');
import { IStore } from 'redux/IStore';
import { Button, ErrorSummary, LoadingOverlayWrapper } from 'components';
import {
	getBookingTemplatesById,
	getBookingTemplatesByOffice,
	removeBookingTemplates,
	resetBookingTemplatesData,
} from 'redux/modules/shop/bookingtemplates';
import { InjectedFormProps, ITranslateProps } from 'models/common';
import { getOfficeStaff, getShopCard } from 'redux/modules/shop/shopcard';
import { getServiceCategoriesDomainValues } from 'redux/modules/domainvalues';
import { deleteServiceGroups, getOfficeServiceGroups } from 'redux/modules/shop/serviceGroups';
import { IBundleForm, IBundleFormValues, IServicesList } from 'models/shop/shopBookingTemplates';
import { BasicInformation } from './BasicInformation';
import renderIcon from 'helpers/renderIcon';
import { BundleSettings } from './BundleSettings';
import { readURLSearchParams, uniq } from 'helpers/common';
import { v4 as uuidv4 } from 'uuid';
import { addServiceGroup, updateServiceGroup } from 'redux/modules/shop/serviceGroups/helpers';
import { IServiceGroup } from 'models/shop/serviceGroups';
import { IApiService, saveGroupedBookingTemplate } from 'redux/modules/shop/bookingtemplates/helpers';
import { fetchBookingTemplateVariants } from 'redux/modules/shop/helpers';
import { actions as notifActions } from 'redux-notifications';
import { closeModal, showModal } from 'redux/modules/modal';
import { IOfficeMember } from 'models/shop/members';
import { scrollErrorIntoView } from 'helpers/validationattributes';

interface IParams {
	shopId: string;
	officeId: string;

	// Used in edit mode
	serviceGroupId: string;
	variantParentId: string;
}

interface IRouterParams {
	mode: 'add' | 'edit';
}

type IRouterProps = RouteComponentProps<IParams, IRouterParams>;

interface IState {
	editHasFetchedResources: boolean;
	selectedServices: string[];
}

interface IProps
	extends InjectedFormProps<IBundleFormValues>,
		IRouterProps,
		ITranslateProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {
	route: any;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopBundlesAddEditForm extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);

		this.state = {
			editHasFetchedResources: false,
			selectedServices: [],
		};

		this.onSubmit = this.onSubmit.bind(this);
		this.handleOpenDeleteModal = this.handleOpenDeleteModal.bind(this);
		this.deleteServices = this.deleteServices.bind(this);
		this.handleServicesSelected = this.handleServicesSelected.bind(this);
	}

	public componentDidMount() {
		const { params, route } = this.props;

		this.props.getShopAndServices(params.shopId, params.officeId, route.mode, params.variantParentId);
		this.props.getOfficeServiceGroups(params.officeId);

		if (!this.props.serviceCategories?.length) {
			this.props.getServiceCategoriesDomainValues();
		}
	}

	public componentDidUpdate(prevProps: IProps) {
		const { params, bookingTemplates, route } = this.props;

		if (
			prevProps.params.shopId !== params.shopId ||
			(route.mode === 'edit' && !bookingTemplates.services?.length && !bookingTemplates.isFetching)
		) {
			this.props.getShopAndServices(params.shopId, params.officeId, route.mode, params.variantParentId);
		}
	}

	public componentWillUnmount() {}

	private redirectToEditServices = () => {
		const { shopId, officeId } = this.props.params;

		this.props.reset();
		browserHistory.replace(`/shop/${shopId}/${officeId}/edit/services/`);
	};

	private onSubmit = (): Promise<any> => {
		// const { initialValues, currentFormValues, params, serviceGroups } = this.props;
		const { currentFormValues, serviceGroups, t } = this.props;
		const { shopId, officeId } = this.props.params;
		const serviceCategory = currentFormValues.values.info.serviceCategory;
		const serviceGroupId = currentFormValues.values.info.serviceGroup || readURLSearchParams(c.SERVICE_GROUP);
		const orderNumber = serviceGroups.filter((sg) => sg.parentId === serviceGroupId).length + 1;
		const parentServiceGroup = serviceGroups.find((sg) => sg.id === serviceGroupId);

		// Set values for add and edit mode
		let originalPrice = 0;
		let discountPrice = 0;
		let totalPrice = 0;
		let totalDuration = 0;
		const allResources: string[] = [];

		this.state.selectedServices.map((serviceId) => {
			const service = this.props.bookingTemplates.services?.find((bt) => bt.id === serviceId);
			originalPrice = originalPrice + service.originalPrice;
			totalDuration = totalDuration + service.duration;
			service.resources.map((resource) => {
				allResources.push((resource as IOfficeMember).id);
			});
		});

		const allResourcesSet = [...Array.from(new Set(allResources))]; // Filter duplicates;

		totalPrice = originalPrice;

		// If custom pricing
		if (
			currentFormValues?.values?.bundle?.pricing.type === 1 &&
			currentFormValues?.values?.bundle?.pricing?.customPrice
		) {
			const customPrice = currentFormValues.values.bundle.pricing.customPrice;
			discountPrice = customPrice - originalPrice;
			totalPrice = customPrice;
		}

		// If percentage discount
		if (currentFormValues?.values?.bundle?.pricing.type === 2 && currentFormValues?.values?.bundle?.pricing?.discount) {
			const discount = currentFormValues.values.bundle.pricing.discount / 100; // percentage
			discountPrice = originalPrice * discount;
			totalPrice = originalPrice - originalPrice * discount;
		}

		if (discountPrice === 0) {
			discountPrice = null;
		}

		const cbtNames = [];

		this.state.selectedServices.map((scId) => {
			const currentBT = this.props.bookingTemplates.services?.find((bt) => bt.id === scId);

			cbtNames.push(currentBT.name);
		});

		if (this.props.route.mode === 'add') {
			const serviceGroupValues = {
				order: orderNumber,
				parentId: serviceGroupId,
				serviceCategoryId: serviceCategory,
				officeId,
				value: currentFormValues.values.info.bundleName,
				type: null,
				id: '',
				color: parentServiceGroup?.color || '#EA3546', // Use default color if parent doesn't have one set
			};

			// Create a new service group for the new bundle
			return this.props
				.addServiceGroup(() => this.props.state, { ...serviceGroupValues })
				.then((res: { id: string }) => {
					// add logic for variations
					const generateBaseVariations = (id?: string) => {
						const servicesArray = [];

						servicesArray.push({
							shopId,
							officeId,
							duration: totalDuration,
							price: totalPrice,
							categoryKey: serviceCategory,
							// resources: resourcesInAll, // Take only resources that are available on all children services
							resources: allResourcesSet,
							serviceGroupId: res.id, // service id
							sku: uuidv4(),
							description: currentFormValues.values.info.serviceDescription,
							order: orderNumber,
							name: currentFormValues.values.info.bundleName,
							published: true, // add toggle in bundle settings?
							id,
							childrenBookingTemplates: this.state.selectedServices,
							customData: JSON.stringify({ isBundle: true, bundleChildrenNames: cbtNames, priceSum: originalPrice }),
						});

						return servicesArray;
					};

					return this.props
						.saveGroupedBookingTemplate(() => this.props.state, generateBaseVariations(), shopId)
						.then(() => {
							this.props.showNotification(t('Shop_Services_ServicesSavedSuccessfully'), 'success');
							this.redirectToEditServices();
						})
						.catch((err) => {
							this.props.showNotification(err ? JSON.stringify(err) : '', 'warning');
							this.redirectToEditServices();
						});
				});
		} else if (this.props.route.mode === 'edit') {
			const serviceGroupBeforeEditing = this.props.serviceGroups.find(
				(sg) => sg.id === this.props.params.serviceGroupId,
			);

			const updateServiceGroup = () => {
				return new Promise((resolve, reject) => {
					if (JSON.stringify(currentFormValues.values.info) !== JSON.stringify(currentFormValues.initial.info)) {
						const serviceGroupToUpdate: IServiceGroup = {
							...serviceGroupBeforeEditing,
							value: currentFormValues.values.info?.bundleName,
							parentId: currentFormValues.values.info?.serviceGroup,
							serviceCategoryId: currentFormValues.values.info?.serviceCategory,
							color: parentServiceGroup?.color || '#EA3546', // Use default color if parent doesn't have one set
						};

						this.props
							.updateServiceGroup(() => this.props.state, serviceGroupToUpdate.id, serviceGroupToUpdate)
							.then(() => {
								this.props.showNotification(t('Shop_Services_ServicesSavedSuccessfully'), 'success');
								resolve(true);
							})
							.catch((err) => {
								this.props.showNotification(err ? JSON.stringify(err) : '', 'warning');
								reject(false);
							});
					} else {
						reject(false);
					}
				});
			};

			const updateAddBookingTemplates = () => {
				return new Promise((resolve, reject) => {
					if (
						JSON.stringify(currentFormValues.values.bundle) !== JSON.stringify(currentFormValues.initial.bundle) ||
						JSON.stringify(currentFormValues.initial.initialChildrenBookingTemplateIds) !==
							JSON.stringify(this.state.selectedServices) ||
						JSON.stringify(currentFormValues.values.info) !== JSON.stringify(currentFormValues.initial.info)
					) {
						const servicesToDispatch: IApiService[] = [];

						const serviceId = currentFormValues.initial.bundle.info.bookingTemplateId;
						const initialBundleValues = this.props.bookingTemplates.services?.find((ser) => ser.id === serviceId);
						const bundleValues = currentFormValues.values.bundle;

						const newBundleValues = {
							...initialBundleValues,
							price: totalPrice,
							published: bundleValues.bundleSettings.bookability,
							// resources: resourcesInAll, // Take only resources that are available on all children services
							resources: allResourcesSet,
							childrenBookingTemplates: this.state.selectedServices,
							duration: totalDuration,
							categpryKey: currentFormValues.values.info?.serviceCategory,
							description: currentFormValues.values.info?.serviceDescription,
							name: currentFormValues.values.info?.bundleName,
							customData: JSON.stringify({ isBundle: true, bundleChildrenNames: cbtNames, priceSum: originalPrice }),
						};

						servicesToDispatch.push(newBundleValues);

						// Dispatch functions

						// updateBookingTemplate does not work as BE throws an error because of childrenBookingTemplates so we use saveGroupedBookingTemplate instead
						this.props
							.saveGroupedBookingTemplate(
								() => this.props.state,
								servicesToDispatch,
								shopId,
								initialBundleValues.variantParentId,
							)
							.then(() => {
								this.props.showNotification(t('Shop_Services_ServicesSavedSuccessfully'), 'success');
								resolve(true);
							});
					} else {
						reject(false);
					}
				});
			};

			return Promise.allSettled([updateServiceGroup(), updateAddBookingTemplates()])
				.then(() => {
					this.redirectToEditServices();
				})
				.catch((err) => {
					this.props.showNotification(err ? JSON.stringify(err) : '', 'warning');
				});
		}
	};

	private handleOpenDeleteModal = () => {
		const { t, showModal, params } = this.props;
		const dialogContent = (
			<>
				<h1>{t('BSPortal_Common_ConfirmationDialog_Sure')}</h1>
				<p className="p14">{t('User_ShopCard_Services_Action_ThisActionWillDelete')}</p>
			</>
		);

		showModal('ConfirmationDialog', {
			confirmAction: this.deleteServices,
			value: params.variantParentId,
			yesButtonText: t('User_ShopCard_Services_DeleteService'),
			noButtonText: t('BSPortal_Common_Cancel'),
			content: dialogContent,
		});
	};

	private deleteServices = (confirm: boolean) => {
		const {
			closeModal,
			removeBookingTemplates,
			deleteServiceGroups,
			servicesToEdit,
			t,
			showNotification,
			currentFormValues,
		} = this.props;
		const name = currentFormValues.initial.info.bundleName;
		if (confirm && servicesToEdit.length) {
			const serviceGroupIds = uniq(servicesToEdit.map((s) => s.serviceGroupId).filter(Boolean));
			return removeBookingTemplates(servicesToEdit.map((s) => s.id))
				.then(() => deleteServiceGroups(serviceGroupIds))
				.then(() => {
					showNotification(`${name} - ${t('User_ShopCard_Bundle_Notification_Deleted')}`, 'success');
					closeModal();
					this.redirectToEditServices();
				})
				.catch((err) => {
					this.props.showNotification(`${name} - ${err}`, 'warning');
				});
		}

		closeModal();
	};

	private renderBasicInformation = () => {
		const { shopCard, serviceCategories, serviceGroups } = this.props;
		const shopTypes = shopCard?.shopTypes;

		return (
			<BasicInformation
				shopTypes={shopTypes}
				serviceCategories={serviceCategories}
				serviceGroups={serviceGroups}
				currentFormValues={this.props.currentFormValues}
				change={this.props.change}
			/>
		);
	};

	private renderServiceSettings = () => {
		return (
			<BundleSettings
				officeId={this.props.params.officeId}
				shopCard={this.props.shopCard}
				currentFormValues={this.props.currentFormValues}
				change={this.props.change}
				mode={this.props.route.mode}
				servicesToEdit={this.props.servicesToEdit}
				editModeServiceGroupId={this.props.params.serviceGroupId}
				serviceGroups={this.props.serviceGroups}
				serviceCategories={this.props.serviceCategories}
				bookingTemplates={this.props.bookingTemplates}
				initialize={this.props.initialize}
				openAddServicesToBundleModal={this.props.openAddServicesToBundleModal}
				handleServicesSelected={this.handleServicesSelected}
				selectedServices={this.state.selectedServices}
			/>
		);
	};

	private handleServicesSelected = (serviceIds: string[]) => {
		this.setState({ selectedServices: serviceIds });
	};

	private get isFetching(): boolean {
		return (
			this.props.isFetchingBookingTemplates ||
			this.props.isFetchingServiceGroups ||
			this.props.isFetchingServiceCategories ||
			this.props.isFetchingShopCard
		);
	}

	render() {
		const { route, params, handleSubmit, t } = this.props;

		const headerContent = (
			<div className="shopBundlesAddEdit__header">
				{route.mode === 'add' ? (
					<>
						<p className="shopBundlesAddEdit__header__upperText">{t('User_ShopCard_Services_AddNewBundle')}</p>
						<div className="shopBundlesAddEdit__header__titleContainer">
							{renderIcon('arrow_left', 18, 18, 'singleAddEditService__header__back', () => {
								browserHistory.push(`/shop/${params.shopId}/${params.officeId}/edit/services`);
							})}
							<h1 className="shopBundlesAddEdit__header__title">{t('User_ShopCard_Services_NewBundle')}</h1>
						</div>
					</>
				) : (
					<>
						<p className="shopBundlesAddEdit__header__upperText">{t('User_ShopCard_Services_EditExistingBundle')}</p>
						<div className="shopBundlesAddEdit__header__titleContainer">
							{renderIcon('arrow_left', 18, 18, 'singleAddEditService__header__back', () => {
								browserHistory.push(`/shop/${params.shopId}/${params.officeId}/edit/services`);
							})}
							<h1 className="shopBundlesAddEdit__header__title">{t('User_ShopCard_Services_EditBundle')}</h1>
						</div>
					</>
				)}
			</div>
		);

		const bottomContent = (
			<div
				className={`shopBundlesAddEdit__bottomContainer ${route.mode === 'add' ? 'serviceAddMode' : 'serviceEditMode'}`}
			>
				<div className="container">
					<div className="row justify-content-center">
						<div className="col-md-8 col-12">
							<div className="shopBundlesAddEdit__bottomContainer__buttonsContainer">
								{route.mode === 'edit' && (
									<Button onClick={() => this.handleOpenDeleteModal()}>
										{renderIcon('delete')} {t('BSPortal_Common_Delete')}
									</Button>
								)}
								<Button type="submit" disabled={!this.state.selectedServices?.length || this.props.submitting}>
									{this.props.submitting ? t('BSPortal_Common_Saving') : t('BSPortal_Common_Save')}
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		);

		return (
			<>
				<LoadingOverlayWrapper
					active={this.isFetching || this.props.submitting}
					className="container shopBundlesAddEditContainer"
				>
					<div className="row justify-content-center">
						<div className="col-md-8 col-12">
							{headerContent}
							<form onSubmit={handleSubmit(this.onSubmit)}>
								<div className="profileError">
									<ErrorSummary {...this.props.bookingTemplates} />
								</div>
								{this.renderBasicInformation()}
								{this.renderServiceSettings()}
								{bottomContent}
							</form>
						</div>
					</div>
				</LoadingOverlayWrapper>
			</>
		);
	}
}

function mapStateToProps(state: IStore, _ownProps: IRouterProps) {
	let currentFormValues: IBundleForm = null;
	let servicesToEdit: IServicesList[] = null; // For edit mode (create and fill)
	const { variantParentId, serviceGroupId } = _ownProps.params;
	const route: any = _ownProps.route;

	if (route?.mode === 'edit' && variantParentId && serviceGroupId && state.shopBookingTemplates?.services.length) {
		servicesToEdit = state.shopBookingTemplates.services?.filter(
			(service) => service.serviceGroupId === serviceGroupId,
		);
	}

	if (state.form[c.FORM_NAME_ADD_EDIT_BUNDLE] && state.form[c.FORM_NAME_ADD_EDIT_BUNDLE].values) {
		currentFormValues = state.form[c.FORM_NAME_ADD_EDIT_BUNDLE];
	}

	return {
		state,
		currentFormValues,
		servicesToEdit,
		shopCard: state.shopcard.shopcard,
		serviceCategories: state.domainValues.serviceCategories,
		serviceGroups: state.serviceGroups.serviceGroups,
		bookingTemplates: state.shopBookingTemplates,
		isFetchingBookingTemplates: state.shopBookingTemplates.isFetching,
		isFetchingServiceGroups: state.serviceGroups.isFetching,
		isFetchingServiceCategories: state.domainValues.isFetchingServiceCategories,
		isFetchingShopCard: state.shopcard.isFetching,
	};
}

function mapDispatchToProps(dispatch: Dispatch<IStore>, _ownProps: IRouterProps) {
	return {
		getShopAndServices: (shopId: string, officeId: string, mode: 'add' | 'edit', variantParentId) => {
			if (!shopId || !officeId) {
				return;
			}
			dispatch(resetBookingTemplatesData());
			dispatch(getShopCard(shopId, false, true, false, officeId));
			dispatch(getBookingTemplatesByOffice(officeId, true)) // Bundles need all of the office's BT's for selection
				.then((res) => {
					if (mode === 'edit' && variantParentId) {
						const services = res?.payload?.services;
						const bundleService = services.find((bt) => bt.variantParentId === variantParentId); // Bundle should be only one booking template

						if (bundleService) {
							dispatch(getBookingTemplatesById(bundleService.id, true, false, true));
						}
					}
				});
		},
		getBookingTemplatesById: (
			bookingTemplateId: string,
			loadResources?: boolean,
			loadChildrenBookingTemplates?: boolean,
		) => dispatch(getBookingTemplatesById(bookingTemplateId, loadResources, false, loadChildrenBookingTemplates)),
		getServiceCategoriesDomainValues: () => dispatch(getServiceCategoriesDomainValues()),
		getOfficeStaff: (officeId: string) => dispatch(getOfficeStaff(officeId, true)),
		getOfficeServiceGroups: (officeId: string) => dispatch(getOfficeServiceGroups(officeId)),
		addServiceGroup: (getState: () => IStore, serviceGroup: IServiceGroup) => addServiceGroup(getState, serviceGroup),
		saveGroupedBookingTemplate: (
			getState: () => IStore,
			services: IApiService[],
			shopId: string,
			variantParentId?: string,
		) => saveGroupedBookingTemplate(getState, services, shopId, variantParentId),
		fetchBookingTemplateVariants: (getState: () => IStore, variantParentId: string) =>
			fetchBookingTemplateVariants(getState, variantParentId, true, false),
		showNotification: (message: string, kind: 'success' | 'info' | 'warning' | 'danger', dismissAfter = 4000) =>
			dispatch(notifActions.notifSend({ message, kind, dismissAfter })),
		showModal: (type: string, modalProps: any) => dispatch(showModal(type, modalProps)),
		closeModal: () => dispatch(closeModal()),
		deleteServiceGroups: (groupIds: string[]) => dispatch(deleteServiceGroups(groupIds)),
		removeBookingTemplates: (serviceIds: string[]) =>
			dispatch(removeBookingTemplates(_ownProps.params.officeId, serviceIds)),
		updateServiceGroup: (getState: () => IStore, serviceGroupId: string, serviceGroup: IServiceGroup) =>
			updateServiceGroup(getState, serviceGroupId, serviceGroup),
		openAddServicesToBundleModal: (
			serviceCategories,
			serviceGroups,
			bookingTemplates,
			mode,
			handleServicesSelected,
			selectedServices,
			currencyKey,
		) => {
			const props = {
				serviceCategories,
				serviceGroups,
				bookingTemplates,
				mode,
				handleServicesSelected,
				selectedServices,
				currencyKey,
			};

			dispatch(showModal('AddServicesToBundle', props));
		},
	};
}

let ShopBundlesAddEdit = reduxForm(
	{
		form: c.FORM_NAME_ADD_EDIT_BUNDLE,
		enableReinitizalize: true,
		onSubmitFail: () => scrollErrorIntoView(),
	},
	mapStateToProps,
)(ShopBundlesAddEditForm);

ShopBundlesAddEdit = connect(mapStateToProps, mapDispatchToProps)(ShopBundlesAddEdit);

export default ShopBundlesAddEdit;
